import { Navigate, Route, Routes } from "react-router-dom";
import { PlatformLayout } from "./platform/components/PlatformLayout";
import { LandingPage } from "./platform/pages/LandingPage";
import { AboutPage } from "./platform/pages/AboutPage";
import { AboutAdminPage } from "./platform/pages/AboutAdminPage";
import { NewsPage } from "./platform/pages/NewsPage";
import { NewsAdminPage } from "./platform/pages/NewsAdminPage";
import { NotifyEmployeesPage } from "./platform/pages/NotifyEmployeesPage";
import { BenefitsPage } from "./benefits/pages/BenefitsPage";
import { BenefitsAdminPage } from "./benefits/pages/BenefitsAdminPage";
import { CafeteriaPage } from "./cafeteria/pages/CafeteriaPage";
import { CafeteriaCounterPage } from "./cafeteria/pages/CafeteriaCounterPage";
import { CafeteriaAdminPage } from "./cafeteria/pages/CafeteriaAdminPage";
import { PoliciesPage } from "./policies/pages/PoliciesPage";
import { PoliciesAdminPage } from "./policies/pages/PoliciesAdminPage";
import { SpeakUpPage } from "./speakup/pages/SpeakUpPage";
import { SpeakUpAdminPage } from "./speakup/pages/SpeakUpAdminPage";
import { AppLayout } from "./gym/components/AppLayout";
import { ProtectedRoute } from "./gym/components/ProtectedRoute";
import { RequireProfile } from "./gym/components/RequireProfile";
import { GymSchedulePage } from "./gym/pages/GymSchedulePage";
import { GymComingSoonPage } from "./gym/pages/GymComingSoonPage";
import { MyBookingsPage } from "./gym/pages/MyBookingsPage";
import { ProfileSetupPage } from "./gym/pages/ProfileSetupPage";
import { CoachCheckInPage } from "./gym/pages/CoachCheckInPage";
import { AdminPage } from "./gym/pages/AdminPage";
import { AnalyticsAdminPage } from "./platform/pages/AnalyticsAdminPage";
import { usePageViewTracking } from "./platform/usePageViewTracking";

// Gym isn't fully live yet — show a "coming soon" placeholder on the main schedule page
// instead of the real schedule/booking flow. Flip to true once it's ready; the other gym
// routes (bookings, profile, coach, admin) are untouched.
const SHOW_GYM_SCHEDULE = false;

export function App() {
  usePageViewTracking();

  return (
    <Routes>
      <Route path="/" element={<PlatformLayout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              <LandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="about"
          element={
            <ProtectedRoute>
              <AboutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="about/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AboutAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AnalyticsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="news"
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="news/admin"
          element={
            <ProtectedRoute requireAdmin>
              <NewsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="benefits"
          element={
            <ProtectedRoute>
              <BenefitsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="benefits/admin"
          element={
            <ProtectedRoute requireAdmin>
              <BenefitsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cafeteria"
          element={
            <ProtectedRoute>
              <CafeteriaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cafeteria/counter"
          element={
            <ProtectedRoute requireCafeteria>
              <CafeteriaCounterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cafeteria/admin"
          element={
            <ProtectedRoute requireAdmin>
              <CafeteriaAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="policies"
          element={
            <ProtectedRoute>
              <PoliciesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="policies/admin"
          element={
            <ProtectedRoute requireAdmin>
              <PoliciesAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="speakup"
          element={
            <ProtectedRoute>
              <SpeakUpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="speakup/admin"
          element={
            <ProtectedRoute requireAdmin>
              <SpeakUpAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications/admin"
          element={
            <ProtectedRoute requireAdmin>
              <NotifyEmployeesPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/gym" element={<AppLayout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              {SHOW_GYM_SCHEDULE ? (
                <RequireProfile>
                  <GymSchedulePage />
                </RequireProfile>
              ) : (
                <GymComingSoonPage />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="bookings"
          element={
            <ProtectedRoute>
              <RequireProfile>
                <MyBookingsPage />
              </RequireProfile>
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfileSetupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="coach"
          element={
            <ProtectedRoute requireCoach>
              <CoachCheckInPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
